import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

type Solution = {
    solutionKey: any;
    title: string;
    sub_title: string;
    bgColor: string;
    bgGradient: string;
};

type QuivverSolutionsProps = {
    quivverProject: any
}

const QuivverSolutions: React.FC<QuivverSolutionsProps> = ({ quivverProject }) => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: false, amount: 0.2 });

    // Container animation variant with stagger
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    };

    // Bottom row container variant
    const bottomRowVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.1,
                duration: 0.6
            }
        }
    };

    return (
        <section className="w-full bg-[#141414] py-12 sm:py-16 md:py-20 lg:py-24">
            <motion.h2
                className="text-center text-[#51C5FF] font-extrabold uppercase text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-12 sm:mb-16 md:mb-20"
                initial={{ opacity: 0, y: -60, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                    duration: 0.8,
                    ease: [0.25, 0.1, 0.25, 1.0]
                }}
                viewport={{ once: false, amount: 0.3 }}
            >
                Solutions
            </motion.h2>

            <motion.div
                className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 px-4 sm:px-6"
                ref={containerRef}
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                {/* Top row */}
                {quivverProject.quivverProjectDeatils.quivverSolutions.slice(0, 3).map((item: any, index: number) => {
                    return (
                        <SolutionCard
                            key={item.key || index}
                            solutionKey={item.key}
                            {...item}
                            index={index}
                            isInView={isInView}
                        />
                    )
                })}

                {/* Bottom row centered */}
                <motion.div
                    className="md:col-span-3 flex flex-col md:flex-row justify-center gap-6 sm:gap-8 md:gap-10 mt-4 sm:mt-6"
                    variants={bottomRowVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    {quivverProject.quivverProjectDeatils.quivverSolutions.slice(3).map((item: any, index: number) => {
                        return (
                            <SolutionCard
                                key={item.key || index + 3}
                                solutionKey={item.key}
                                {...item}
                                index={index + 3}
                                isInView={isInView}
                            />
                        )
                    })}
                </motion.div>
            </motion.div>
        </section>
    );
};

type SolutionCardProps = Solution & {
    index: number;
    isInView: boolean;
};

const SolutionCard: React.FC<SolutionCardProps> = ({
    solutionKey,
    title,
    sub_title,
    bgColor,
    bgGradient,
    index,
    isInView,
}) => {
    // Enhanced card animation variants - alternating from left, right, and top
    const getCardDirection = (idx: number) => {
        const direction = idx % 3;
        if (direction === 0) {
            // From left
            return { x: -80, y: 0 };
        } else if (direction === 1) {
            // From right
            return { x: 80, y: 0 };
        } else {
            // From top
            return { x: 0, y: -60 };
        }
    };

    const direction = getCardDirection(index);

    const cardVariants = {
        hidden: {
            opacity: 0,
            y: direction.y,
            x: direction.x,
            scale: 0.85,
            rotateX: direction.y === 0 ? 0 : 15
        },
        visible: {
            opacity: 1,
            y: 0,
            x: 0,
            scale: 1,
            rotateX: 0,
            transition: {
                duration: 0.7,
                ease: [0.25, 0.1, 0.25, 1.0],
                type: "spring",
                stiffness: 100,
                damping: 15
            }
        },
        hover: {
            scale: 1.05,
            y: -8,
            rotateX: -5,
            transition: {
                duration: 0.4,
                ease: "easeInOut"
            }
        }
    };

    // Text animation variants
    const titleVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.5,
                delay: 0.2,
                ease: "easeOut"
            }
        }
    };

    const textVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                delay: 0.3,
                ease: "easeOut"
            }
        }
    };

    return (
        <motion.div
            className="relative rounded-2xl w-full overflow-hidden"
            style={{
                background: `linear-gradient(129.28deg, ${bgGradient} 0%, rgba(95, 17, 108, 0) 80.78%)`
            }}
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            whileHover="hover"
        >
            <div className={`relative w-full p-6 sm:p-8 text-white overflow-hidden pr-12 sm:pr-14`}>
                <motion.h3
                    className="relative z-10 uppercase font-extrabold text-xl sm:text-2xl mb-3 sm:mb-4"
                    variants={titleVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    {title}
                </motion.h3>

                <motion.p
                    className="relative z-10 text-white/80 text-base sm:text-lg md:text-xl leading-relaxed pb-8 sm:pb-10"
                    variants={textVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                >
                    {sub_title}
                </motion.p>
            </div>
            <motion.div
                className={`absolute bottom-3 right-3 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 ${bgColor} rounded-full flex items-center justify-center text-lg sm:text-xl md:text-2xl font-black text-black shadow-lg z-100`}
                initial={{ scale: 0, rotate: -180, opacity: 0 }}
                animate={isInView ? {
                    scale: 1,
                    rotate: 0,
                    opacity: 1
                } : {
                    scale: 0,
                    rotate: -180,
                    opacity: 0
                }}
                transition={{
                    duration: 0.6,
                    delay: index * 0.1 + 0.4,
                    type: "spring",
                    stiffness: 200,
                    damping: 15
                }}
                whileHover={{
                    scale: 1.15,
                    rotate: 360,
                    transition: { duration: 0.4 }
                }}
            >
                {solutionKey}.
            </motion.div>
        </motion.div>
    );
};

export default QuivverSolutions;
